/**
 * Operational — Staffing ratios table (OR-1147).
 *
 * Per facility per year row. The facility identity columns
 * (Name / Address / City / State) bind to the shared SoT
 * (`useFacilities`), so edits propagate to Schedule and back. The
 * staffing-ratio columns (RN / LPN / CNA / Total — each with a paired
 * "State average" read-only) come from the staged Operational hook
 * (`useOperationalStaffing`).
 *
 * Year filter: rows are filtered to the active year. Adding a row
 * here:
 *   1. Creates a new facility on the SoT (so Schedule, Regulatory,
 *      … pick it up).
 *   2. Stages a new staffing-ratio row pinned to the same facility +
 *      the active year.
 *
 * Mobile (`<md`): sticky columns are dropped per the Figma development
 * annotation — without that the user can't scroll the table sideways.
 */
import { useMemo } from "react";
import { Users01 } from "@untitledui/icons";
import { Table } from "@tricura/atlas-ui";
import { EditableCellProvider, EditableTextCell, InvalidShell } from "../../../../components/editable-cell";
import { ARCHIVED_ROW_VISUAL } from "../../../../components/facility-archive-row";
import { FacilityNameAutocomplete } from "../../../../components/facility-name-autocomplete";
import { FacilityRowActionsMenu } from "../../../../components/facility-row-actions-menu";
import { RowDeleteMenu } from "../../../../components/row-actions-menu";
import { findFacilityForRow } from "../../../../domain/facility-archive";
import { StickyColDivider } from "../../../../components/sticky-col-divider";
import { TableEmptyState } from "../../../../components/table-empty-state";
import { useFacilities } from "../../../../hooks/use-facilities";
import { useOperationalStaffing } from "../../../../hooks/use-operational-staffing";
import type { CatalogFacility } from "../../../../api";
import type { Facility } from "../../../../types/facility";
import type { StaffingRatioWritePayload } from "../../../../types/operational";
import type { OperationalDraft } from "../index";

const W = {
    rowNum: 48,
    // Fixed 240px to match Schedule — Facility Name wraps/scrolls instead of
    // taking a wide frozen column (Andressa, jun-22).
    facilityName: 240,
    address: 240,
    city: 160,
    state: 120,
    staffing: 140,
    actions: 64,
} as const;

const fixed = (px: number) => ({ width: px, minWidth: px });

/** Stable empty array so the facility join keeps a constant identity while the
 *  SoT is still loading. */
const EMPTY_FACILITIES: Facility[] = [];

// Desktop-only sticky chrome. Mobile (<md) drops the sticky positioning
// per the Figma development annotation — fixed columns would block
// sideways scrolling on a narrow viewport.
// `max-md:static!` cancels the inline `left` offset on mobile (cells are
// position:relative) so the frozen columns flow + scroll cleanly below md.
const STICKY_NUM_HEAD = "md:sticky md:z-10 md:bg-secondary max-md:static!";
// Facility Name is the last pinned column; it hosts the divider line
// (md-only, since columns are only sticky at md+ — Andressa review).
const STICKY_NAME_HEAD = "md:sticky md:z-10 md:bg-secondary md:pl-3 max-md:static!";
const STICKY_NUM_CELL = "md:sticky md:z-10 md:bg-primary md:text-tertiary max-md:static!";
const STICKY_NAME_CELL = "md:sticky md:z-10 md:bg-primary max-md:static!";
const STICKY_RIGHT_HEAD =
    "md:sticky md:right-0 md:z-10 md:bg-secondary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";
const STICKY_RIGHT_CELL =
    "md:sticky md:right-0 md:z-10 md:bg-primary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]";

const READONLY_CELL = "text-secondary text-sm tabular-nums";

/** Decimal sanitizer for staffing values — "3.8" / "3.8" stay as
 *  typed; letters and stray punctuation get stripped. */
function decimal(raw: string): string {
    // Keep digits + a single decimal point. Trims duplicate points.
    const cleaned = raw.replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot === -1) return cleaned;
    return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
}

interface StaffingRatiosTableProps {
    submissionId: string | undefined;
    year: number;
    /** Not-yet-saved rows (facility autocomplete) — owned by the page. */
    drafts: OperationalDraft[];
    showErrors: boolean;
    onDraftChange: (id: string, patch: Partial<OperationalDraft>) => void;
    onDraftPickFacility: (id: string, facility: CatalogFacility) => void;
    onDraftRemove: (id: string) => void;
    onAddDraft: () => void;
}

export function StaffingRatiosTable({
    submissionId,
    year,
    drafts,
    showErrors,
    onDraftChange,
    onDraftPickFacility,
    onDraftRemove,
    onAddDraft,
}: StaffingRatiosTableProps) {
    const { state: staffingState, update: updateStaffing, remove: removeStaffing } =
        useOperationalStaffing(submissionId);

    /**
     * The facility behind each staffing row (OR-1431). Staffing rows carry the
     * GLOBAL catalog facility id, never the `SubmissionLocation.id`, so both the
     * archived styling and the ⋮'s Archive/Restore have to join back to the
     * facility SoT — the staffing endpoint knows nothing about either.
     *
     * The row itself stays editable and deletable when its facility is archived:
     * it's one year's staffing, not the facility. Rows that resolve to no
     * location just render normally and get no Archive item.
     */
    const { state: facilitiesState, update: updateFacility } = useFacilities(submissionId);
    const facilityList = useMemo(
        () => (facilitiesState.status === "ready" ? facilitiesState.data : EMPTY_FACILITIES),
        [facilitiesState],
    );

    // Source of truth: the Staffing Ratio endpoint. One row per staffing
    // entry for the active year; the facility identity (name/address/city/
    // state) is read-only, carried inline by the response. Facilities with no
    // staffing row simply don't appear here. Sorted by facility name.
    const rows = useMemo(() => {
        const staffing = staffingState.status === "ready" ? staffingState.data : [];
        return staffing
            .filter((s) => s.year === year)
            .sort((a, b) => a.facilityName.localeCompare(b.facilityName));
    }, [staffingState, year]);

    const isLoading = staffingState.status === "loading";
    const isEmpty = !isLoading && rows.length === 0 && drafts.length === 0;

    const setStaffing = (id: string, patch: StaffingRatioWritePayload) => void updateStaffing(id, patch);
    const removeRow = (staffingId: string) => void removeStaffing(staffingId);
    const setFacilityArchived = (facilityId: string, isExcluded: boolean) =>
        void updateFacility(facilityId, { isExcluded });
    /** Rows paired with the facility behind them, resolved once per render
     *  instead of twice per row (the archived styling and the ⋮ both need it). */
    const rowsWithFacility = useMemo(
        () =>
            rows.map((stRow) => {
                const facility = findFacilityForRow(facilityList, stRow);
                return { stRow, facility, archived: facility?.isExcluded === true };
            }),
        [rows, facilityList],
    );

    return (
        <EditableCellProvider>
            <div className="relative flex flex-col overflow-hidden lg:min-h-0 lg:flex-1">
                <Table
                    aria-label="Staffing ratios"
                    wrapperClassName="overflow-auto border-b border-secondary lg:min-h-0 lg:flex-1"
                    className="[&_tbody_tr:last-child]:border-b-0!"
                >
                    <Table.Header className="bg-secondary sticky top-0 z-20">
                        <Table.Head
                            id="rowNum"
                            label="#"
                            isRowHeader
                            className={STICKY_NUM_HEAD}
                            style={{ ...fixed(W.rowNum), left: 0 }}
                        />
                        <Table.Head
                            id="facilityName"
                            label="Facility Name"
                            className={STICKY_NAME_HEAD}
                            style={{ ...fixed(W.facilityName), left: W.rowNum }}
                        >
                            <StickyColDivider mdOnly />
                        </Table.Head>
                        <Table.Head id="address" label="Address" style={fixed(W.address)} />
                        <Table.Head id="city" label="City" style={fixed(W.city)} />
                        <Table.Head id="state" label="State" style={fixed(W.state)} />
                        <Table.Head id="rnStaffing" label="RN Staffing" style={fixed(W.staffing)} />
                        <Table.Head id="rnStAvg" label="RN St Avg" style={fixed(W.staffing)} />
                        <Table.Head id="lpnStaffing" label="LPN Staffing" style={fixed(W.staffing)} />
                        <Table.Head id="lpnStAvg" label="LPN St Avg" style={fixed(W.staffing)} />
                        <Table.Head id="cnaStaffing" label="CNA Staffing" style={fixed(W.staffing)} />
                        <Table.Head id="cnaStAvg" label="CNA St Avg" style={fixed(W.staffing)} />
                        <Table.Head id="totalStaffing" label="Total Staffing" style={fixed(W.staffing)} />
                        <Table.Head id="totalStAvg" label="Total St Avg" style={fixed(W.staffing)} />
                        <Table.Head
                            id="actions"
                            label=""
                            className={STICKY_RIGHT_HEAD}
                            style={fixed(W.actions)}
                        />
                    </Table.Header>
                    <Table.Body>
                        {rowsWithFacility.map(({ stRow, facility, archived }, idx) => (
                            <Table.Row
                                id={stRow.id}
                                key={stRow.id}
                                className={archived ? ARCHIVED_ROW_VISUAL : undefined}
                            >
                                <Table.Cell className={STICKY_NUM_CELL} style={{ ...fixed(W.rowNum), left: 0 }}>
                                    {idx + 1}
                                </Table.Cell>
                                {/* Facility identity is read-only — it's carried by the
                                  * staffing response (the SoT); edit it in Schedule. */}
                                <Table.Cell className={STICKY_NAME_CELL} style={{ ...fixed(W.facilityName), left: W.rowNum }}>
                                    <span className={READONLY_CELL} data-autotest="operational-facility-name">
                                        {stRow.facilityName || "—"}
                                    </span>
                                    <StickyColDivider mdOnly />
                                </Table.Cell>
                                <Table.Cell style={fixed(W.address)}>
                                    <span className={READONLY_CELL} data-autotest="operational-facility-address">
                                        {stRow.address || "—"}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.city)}>
                                    <span className={READONLY_CELL} data-autotest="operational-facility-city">
                                        {stRow.city || "—"}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.state)}>
                                    <span className={READONLY_CELL} data-autotest="operational-facility-state">
                                        {stRow.state || "—"}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.staffing)}>
                                    <EditableTextCell
                                        ariaLabel="RN Staffing"
                                        placeholder="0.0"
                                        data-autotest={`operational-rn-staffing-${year}`}
                                        value={stRow.rnStaffing}
                                        onChange={(v) => setStaffing(stRow.id, { rnStaffing: v })}
                                        inputMode="decimal"
                                        sanitize={decimal}
                                    />
                                </Table.Cell>
                                <Table.Cell style={fixed(W.staffing)}>
                                    <span className={READONLY_CELL} data-autotest={`operational-rn-staffing-avg-${year}`}>
                                        {stRow.rnStAvg || "—"}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.staffing)}>
                                    <EditableTextCell
                                        ariaLabel="LPN Staffing"
                                        placeholder="0.0"
                                        value={stRow.lpnStaffing}
                                        data-autotest={`operational-lpn-staffing-${year}`}
                                        onChange={(v) => setStaffing(stRow.id, { lpnStaffing: v })}
                                        inputMode="decimal"
                                        sanitize={decimal}
                                    />
                                </Table.Cell>
                                <Table.Cell style={fixed(W.staffing)}>
                                    <span className={READONLY_CELL} data-autotest={`operational-lpn-staffing-avg-${year}`}>
                                        {stRow.lpnStAvg || "—"}
                                    </span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.staffing)}>
                                    <EditableTextCell
                                        ariaLabel="CNA Staffing"
                                        placeholder="0.0"
                                        value={stRow.cnaStaffing}
                                        data-autotest={`operational-cna-staffing-${year}`}
                                        onChange={(v) => setStaffing(stRow.id, { cnaStaffing: v })}
                                        inputMode="decimal"
                                        sanitize={decimal}
                                    />
                                </Table.Cell>
                                <Table.Cell style={fixed(W.staffing)}>
                                    <span className={READONLY_CELL} data-autotest={`operational-cna-staffing-avg-${year}`}>
                                        {stRow.cnaStAvg || "—"}
                                    </span>
                                </Table.Cell>
                                {/* Total Staffing is server-derived (rn+lpn+cna) —
                                  * read-only, same as the state-average columns. */}
                                <Table.Cell style={fixed(W.staffing)}>
                                    <span className={READONLY_CELL} data-autotest={`operational-total-staffing-${year}`}>
                                        {stRow.totalStaffing || "—"}</span>
                                </Table.Cell>
                                <Table.Cell style={fixed(W.staffing)}>
                                    <span className={READONLY_CELL} data-autotest={`operational-total-staffing-avg-${year}`}>
                                        {stRow.totalStAvg || "—"}</span>
                                </Table.Cell>
                                {/* The ⋮ that replaced the trash. Delete removes this
                                  * year's staffing row only — the staffing endpoint has
                                  * no field to archive it with. Archive/Restore acts on
                                  * the row's facility instead (a GLOBAL action, joined
                                  * back to the facility SoT above), cascading to
                                  * Schedule/Regulatory/Loss Runs/Legal; omitted for rows
                                  * with no resolvable facility. */}
                                <Table.Cell className={STICKY_RIGHT_CELL} style={fixed(W.actions)}>
                                    <div className="flex justify-end">
                                        <FacilityRowActionsMenu
                                            facilityName={stRow.facilityName}
                                            isArchived={archived}
                                            canArchive={facility !== undefined}
                                            onArchive={facility ? () => setFacilityArchived(facility.id, true) : undefined}
                                            onRestore={facility ? () => setFacilityArchived(facility.id, false) : undefined}
                                            onDelete={() => removeRow(stRow.id)}
                                            deleteTitle="Delete row"
                                            deleteDescription="Delete this year's staffing row from the submission. This action cannot be undone."
                                            data-autotest="operational-staffing-row-actions-trigger"
                                        />
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                        {drafts.map((draft, dIdx) => (
                            <StaffingDraftRow
                                key={draft.id}
                                draft={draft}
                                index={rows.length + dIdx + 1}
                                showErrors={showErrors}
                                onChange={onDraftChange}
                                onPickFacility={onDraftPickFacility}
                                onRemove={onDraftRemove}
                            />
                        ))}
                    </Table.Body>
                </Table>
                {isEmpty && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <div className="pointer-events-auto">
                            <TableEmptyState
                                icon={Users01}
                                title="Add Staffing ratios"
                                description="Use the button below to start building the Staffing ratios"
                                onAdd={onAddDraft}
                                data-autotest="operational-empty-state-add-row-button"
                            />
                        </div>
                    </div>
                )}
            </div>
        </EditableCellProvider>
    );
}

/** A not-yet-saved Operational row. Facility comes from the catalog
 *  autocomplete (existing only) — on Save the page creates the location +
 *  staffing. Address / City / State auto-fill (read-only) from the pick;
 *  the staffing values are editable. Inline error (same style as
 *  Regulatory) when the user tries to save without a valid facility. */
function StaffingDraftRow({
    draft,
    index,
    showErrors,
    onChange,
    onPickFacility,
    onRemove,
}: {
    draft: OperationalDraft;
    index: number;
    showErrors: boolean;
    onChange: (id: string, patch: Partial<OperationalDraft>) => void;
    onPickFacility: (id: string, facility: CatalogFacility) => void;
    onRemove: (id: string) => void;
}) {
    const set = (patch: Partial<OperationalDraft>) => onChange(draft.id, patch);
    const facilityInvalid = showErrors && !draft.catalog;
    const cf = draft.catalog;
    return (
        <Table.Row id={draft.id}>
            <Table.Cell className={STICKY_NUM_CELL} style={{ ...fixed(W.rowNum), left: 0 }}>
                {index}
            </Table.Cell>
            <Table.Cell className={STICKY_NAME_CELL} style={{ ...fixed(W.facilityName), left: W.rowNum }}>
                <InvalidShell isInvalid={facilityInvalid} errorMessage="Select a facility from the list." reserveSpace={false}>
                    <FacilityNameAutocomplete
                        ariaLabel="Facility Name"
                        placeholder="Type Facility Name"
                        value={draft.facilityName}
                        onChange={(v) => set({ facilityName: v, catalog: null })}
                        onSelect={(facility) => onPickFacility(draft.id, facility)}
                        isInvalid={facilityInvalid}
                    />
                </InvalidShell>
                <StickyColDivider mdOnly />
            </Table.Cell>
            <Table.Cell style={fixed(W.address)}>
                <span className={READONLY_CELL}>{cf?.address || "—"}</span>
            </Table.Cell>
            <Table.Cell style={fixed(W.city)}>
                <span className={READONLY_CELL}>{cf?.city || "—"}</span>
            </Table.Cell>
            <Table.Cell style={fixed(W.state)}>
                <span className={READONLY_CELL}>{cf?.state || "—"}</span>
            </Table.Cell>
            <Table.Cell style={fixed(W.staffing)}>
                <EditableTextCell
                    ariaLabel="RN Staffing"
                    placeholder="0.0"
                    value={draft.rnStaffing}
                    onChange={(v) => set({ rnStaffing: v })}
                    inputMode="decimal"
                    sanitize={decimal}
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.staffing)}>
                <span className={READONLY_CELL}>—</span>
            </Table.Cell>
            <Table.Cell style={fixed(W.staffing)}>
                <EditableTextCell
                    ariaLabel="LPN Staffing"
                    placeholder="0.0"
                    value={draft.lpnStaffing}
                    onChange={(v) => set({ lpnStaffing: v })}
                    inputMode="decimal"
                    sanitize={decimal}
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.staffing)}>
                <span className={READONLY_CELL}>—</span>
            </Table.Cell>
            <Table.Cell style={fixed(W.staffing)}>
                <EditableTextCell
                    ariaLabel="CNA Staffing"
                    placeholder="0.0"
                    value={draft.cnaStaffing}
                    onChange={(v) => set({ cnaStaffing: v })}
                    inputMode="decimal"
                    sanitize={decimal}
                />
            </Table.Cell>
            <Table.Cell style={fixed(W.staffing)}>
                <span className={READONLY_CELL}>—</span>
            </Table.Cell>
            <Table.Cell style={fixed(W.staffing)}>
                <span className={READONLY_CELL}>—</span>
            </Table.Cell>
            <Table.Cell style={fixed(W.staffing)}>
                <span className={READONLY_CELL}>—</span>
            </Table.Cell>
            {/* Same ⋮ as the saved rows, so the actions column doesn't switch
              * controls halfway down the table. A draft was never persisted, so
              * this only discards it. */}
            <Table.Cell className={STICKY_RIGHT_CELL} style={fixed(W.actions)}>
                <div className="flex justify-end">
                    <RowDeleteMenu
                        title="Remove row"
                        description="Discard this row. It hasn't been saved to the submission yet."
                        rowLabel={draft.facilityName}
                        onDelete={() => onRemove(draft.id)}
                    />
                </div>
            </Table.Cell>
        </Table.Row>
    );
}
